import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TaskTester, TrackerTaskParams } from '@app/interfaces/lm/kanban-board';
import { Account } from '@app/interfaces/sys/account';
import { SelectOption } from '@app/interfaces/sys/dict';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { AccountService } from '@services/sys/account.service';
import { ConfirmationService } from '@services/sys/confirmation.service';
import { GANTT_GLOBAL_CONFIG, GanttDate, GanttItem, GanttViewType, NgxGanttComponent } from '@worktile/gantt';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';

interface Task {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
}

interface Tester {
  id: string;
  name: string;
  children?: Task[];
  totalTaskCount: number;
  taskStartRow: number;
  taskEndRow: number;
  hasLoadMore: boolean;
  expanded?: boolean;
  expandable?: boolean;
}

type LoadedTasksMap = Map<string, Task[]>;
type LoadedTesterMap = Map<string, Tester[]>;

@Component({
  selector: 'app-task-tester',
  templateUrl: './task-tester.component.html',
  styleUrl: './task-tester.component.less',
  providers: [
    {
      provide: GANTT_GLOBAL_CONFIG,
      useValue: {
        locale: 'en',
        timeZone: 'America/New_York',
        dateFormat: {
          day: 'MMM d',
          hour: 'HH:mm',
          month: 'MMM',
          quarter: "'Q'q",
          week: "'Week' w, d",
          year: 'yyyy',
          yearMonth: 'yyyy-MM',
          yearQuarter: "yyyy-'Q'Q",
        },
      },
    },
  ],
})
export class TaskTesterComponent implements OnInit, OnChanges {
  @ViewChild('gantt') ganttComponent!: NgxGanttComponent;
  @Input() projectMembers: SelectOption[] = [];
  @Input() accountList: Account[] = [];
  @Input() value: TaskTester[] = [];
  @Input() visibleItems: GanttItem[] = [];
  @Input() className = '';
  @Input() fieldName = 'Tester';
  @Input() clickToEdit = true;
  @Input() isRequired = false;
  @Input() focusedField: string = '';
  @Input() isTester: { category: boolean } = { category: false };
  @Input() inactiveUsers: boolean = false;
  @Output() editSuccess = new EventEmitter();

  items: GanttItem[] = [];
  isEditing = false;
  showGantt = false;
  testerOptions: SelectOption[] = [];
  saveLoading = false;
  currentValue: TaskTester[] = [];
  selectedUserIds: number[] = [];
  accountData: {
    [userId: number]: Account
  } = {};
  initValue: TaskTester[] = [];

  totalTaskCount: number = 0;
  loadedTaskMap: LoadedTasksMap = new Map();
  loadedTesterMap: LoadedTesterMap = new Map();

  dateDefault = dayjs().format('YYYY-MM-DD');
  earliestStart = new GanttDate(dayjs().startOf('day').toDate());
  latestEnd = new GanttDate(dayjs().endOf('day').toDate());
  viewOptions = {
    start: this.earliestStart,
    end: this.latestEnd,
    baseTime: this.earliestStart,
    addAmount: 0
  }

  viewType: GanttViewType = GanttViewType.hour;

  constructor(private message: NzMessageService,
    private accountService: AccountService,
    private kanbanBoardService: KanbanBoardService,
    private confirmationService: ConfirmationService) {
  }

  async ngOnInit(): Promise<void> {
    if (!this.clickToEdit && this.currentValue.length === 0) {
      this.addTester();
    }
    this.setNoDataText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setNoDataText();
    const { accountList, projectMembers, value } = changes;
    if (accountList) {
      this.accountData = this.accountService.formatAccountList(this.accountList || []);
      this.getTesterOptions();
    }
    if (projectMembers) {
      this.getTesterOptions();
    }
    if (value) {
      this.initData();
    }

    this.showGantt = this.focusedField === '' ? this.isTester.category && this.isEditing : this.focusedField === 'Tester';
    if (this.showGantt) {
      this.changeTester();
    }
  }

  initData(): void {
    this.initValue = this.value && this.value.length > 0 ? this.setData() : [];
    this.currentValue = this.initValue;
    if (!this.clickToEdit && this.currentValue.length === 0) {
      this.addTester();
    }
  }

  /** 回显数据格式化 */
  setData(): any {
    return (this.value || []).map(item => {
      return {
        testerId: item.testerId,
        testerName: item.testerName,
        timeIntervals: item.timeIntervals || []
      };
    });
  }

  isMemberActive(userId: number): boolean {
    return this.accountData[userId as number]?.enable ?? true;
  }

  anyInactiveMembers(): boolean {
    const testers = this.currentValue;
    const allMembers = this.accountData;
    let hasInactiveMember = false;
    testers.forEach(tester => {
      const testerId = tester.testerId as number;
      if (!testerId) {
        return;
      }
      if (!allMembers[testerId].enable) {
        hasInactiveMember = true;
      }
    })
    return hasInactiveMember;
  }

  getTesterOptions(): void {
    // const ids = [...new Set(this.projectMembers.map(x => Number(x.value)))];
    this.testerOptions = this.accountList
      .filter(it => it.roleNames.includes('LM_ROLE_TESTER') && it.enable)
      .map(it => {
        const { id, lastName, name } = it;
        return {
          value: id,
          label: `${name} ${lastName}`
        };
      });
  }

  getSelectUserIds(): void {
    this.selectedUserIds = this.currentValue.filter(it => it.testerId).map(it => it.testerId as number);
  }

  // temp: changes chinese text for 'No Data' to english
  setNoDataText() {
    setTimeout(() => {
      const noDataEl = this.ganttComponent?.element.querySelector('.empty-text') || null;
      if (noDataEl && noDataEl.textContent?.includes('没有数据')) {
        noDataEl.textContent = 'No Data';
      }
    });
  }

  convert(userId: string[], start: string, end: string, taskStart?: number, taskEnd?: number): TrackerTaskParams {
    return {
      testerIdIn: userId ?? [],
      subtaskEndRow: 50,
      subtaskStartRow: 0,
      taskEndRow: taskEnd ? taskEnd : 50,
      taskStartRow: taskStart ? taskStart : 0,
      testerEndRow: 50,
      testerStartRow: 0,
      startDate: start,
      endDate: end,
    }
  }

  async getSelectedUserAvailability() {
    const currentValuteIds = this.currentValue.map(value => { return value.testerId?.toString() || "" });
    dayjs.extend(utc);
    const isoUtcStart = dayjs(this.earliestStart.getUnixTime() * 1000).utc().format();
    const isoUtcEnd = dayjs(this.latestEnd.getUnixTime() * 1000).utc().format();
    return await this.kanbanBoardService.loadAllTasksByTester(this.convert(currentValuteIds, isoUtcStart, isoUtcEnd));
  }

  updateTasksByDateRange() {
    this.items.forEach(async (testerItem, index) => {
      const startQuery = this.loadedTesterMap.get(testerItem.id)![0].taskStartRow || 0;
      const endQuery = this.loadedTesterMap.get(testerItem.id)![0].taskEndRow || 50;

      await this.getAllTaskData(Number(testerItem.id), startQuery, endQuery);
      const filteredTasks = this.loadedTaskMap.get(testerItem.id.toString())?.filter(task =>
        (task.endDate.getTime() >= this.earliestStart.getTime() && task.startDate.getTime() <= this.latestEnd.getTime())) || [];

      this.loadedTesterMap.get(testerItem.id)?.forEach(tester => {
        tester.children = [...filteredTasks];
        if (!tester.hasLoadMore && tester.totalTaskCount > (tester.taskEndRow + 1)) {
          tester.hasLoadMore = true;
          const loadMore = {
            id: `loadMore-${testerItem.id}`,
            name: 'Load More',
            startDate: new Date(0),
            endDate: new Date(0),
          };
          tester.children?.push(loadMore);
        } else {
          tester.children = filteredTasks.filter(child => child.id !== `loadMore-${testerItem.id}`) ?? [];
          tester.hasLoadMore = false;
          if (tester.taskEndRow < tester.totalTaskCount) {
            tester.hasLoadMore = true;
            const loadMore = {
              id: `loadMore-${testerItem.id}`,
              name: 'Load More',
              startDate: new Date(0),
              endDate: new Date(0),
            };
            tester.children?.push(loadMore);
          }
        }
      })
      this.convertTesterToGanttItem();
    })
  }

  updateTesterListGantt() {
    const itemIds = this.items.map(item => { return item.id });
    const currentValuteIds = this.currentValue.map(value => { return value.testerId?.toString() });

    // checks that gantt items are only showing what is in current values (selected users)
    itemIds.forEach((itemId) => {
      if (!currentValuteIds.includes(itemId)) {
        const targetId = itemId;
        const indexInItems = this.items.findIndex(item => item.id === targetId);
        this.items.splice(indexInItems, 1);
        this.loadedTesterMap.delete(itemId);
        this.loadedTaskMap.delete(itemId);
      }
    })
    if (this.currentValue.length === 0) {
      this.showGantt = false;
    }
    this.convertTesterToGanttItem();
  }

  async getAllTaskData(testerId: number, taskStart?: number, taskEnd?: number) {
    dayjs.extend(utc);
    const isoUtcStart = dayjs(this.earliestStart.getUnixTime() * 1000).utc().format();
    const isoUtcEnd = dayjs(this.latestEnd.getUnixTime() * 1000).utc().format();
    const response = await this.kanbanBoardService.loadAllTasksByTester(this.convert([testerId.toString()], isoUtcStart, isoUtcEnd, taskStart, taskEnd));

    if (response.length === 0) { return []; }

    const mappedTasks = response[0].tasksArray?.map(task => ({
      id: task.taskId.toString(),
      title: task.taskName,
      timeIntervals: task.timeIntervals
    }));

    const mappedSubTasks = response[0].subtasksArray?.map(subtask => ({
      id: subtask.subtaskId.toString(),
      title: subtask.taskName,
      timeIntervals: subtask.timeIntervals
    }));

    const allTasksArrayComb = [...(mappedTasks || []), ...(mappedSubTasks || [])];
    this.totalTaskCount = response[0].taskCount + response[0].subtaskCount;
    const testerList = this.loadedTesterMap.get(testerId.toString());
    if (testerList && testerList[0]) {
      testerList[0].totalTaskCount = response[0].taskCount + response[0].subtaskCount;
    }

    const existingTasks = this.loadedTaskMap.get(testerId.toString()) || [];
    const combinedTasks = [...existingTasks];

    allTasksArrayComb.forEach(task => {
      task.timeIntervals?.forEach((time, index) => {
        const newTask = {
          id: `${task.id}-${testerId}-${time.startDate}-${time.dueDate}`,
          name: task.title,
          startDate: new Date(time.startDate),
          endDate: new Date(time.dueDate!)
        };
        const alreadyExists = combinedTasks.some(task => task.id === newTask.id);
        if (!alreadyExists) {
          combinedTasks.push(newTask);
        }
      });
    });
    this.loadedTaskMap.set(testerId.toString(), combinedTasks);
    return allTasksArrayComb;
  }

  changeTester(): void {
    this.setNoDataText();
    this.getSelectUserIds();
    this.updateTesterListGantt();
    this.getUpdatedDateRange();
    this.getSelectedUserAvailability().then(info => {
      if (this.currentValue.length > info.length) {
        this.currentValue.forEach(value => {

          if (!value.testerId) { return; }
          const existingTesters = this.loadedTesterMap.get(value.testerId?.toString() || '') || [];

          // checks if user is already existing in loaded tester map, if not, add it
          let testerNameIndex = this.testerOptions.findIndex(tester => tester.value === value.testerId);
          if (!this.loadedTesterMap.has(value.testerId!.toString())) {
            const newTester = {
              id: value.testerId!.toString(),
              name: this.testerOptions[testerNameIndex].label as string,
              children: [],
              totalTaskCount: 0,
              taskEndRow: 50,
              taskStartRow: 0,
              hasLoadMore: (this.loadedTaskMap.get(value.testerId!.toString())?.length || 0) > 50,
              expanded: true,
              expandable: true,
            }
            this.loadedTesterMap.set(value.testerId!.toString(), [...existingTesters, newTester]);
            this.convertTesterToGanttItem();
          }
        })
      }
      // adding each selected tester with tasks to loaded tester map
      info.forEach(async (testerData) => {
        const { testerId, testerName } = testerData;
        if (!this.loadedTesterMap.has(testerId.toString())) {
          await this.getAllTaskData(testerId, 0, 50);
          const tester = {
            id: testerId.toString(),
            name: testerName,
            children: this.loadedTaskMap.get(testerId.toString()) || [],
            totalTaskCount: this.totalTaskCount,
            taskStartRow: 0,
            taskEndRow: 50,
            hasLoadMore: (this.loadedTaskMap.get(testerId.toString())?.length || 0) > 50,
            expanded: true,
            expandable: true
          }
          if (this.totalTaskCount > 50) {
            const loadMore = {
              id: `loadMore-${testerId}`,
              name: 'Load More',
              startDate: new Date(0),
              endDate: new Date(0),
            };
            this.loadedTaskMap.get(testerId.toString())?.push(loadMore);
            tester.hasLoadMore = true;
          }
          this.loadedTesterMap.set(testerId.toString(), [tester]);
        }
        this.convertTesterToGanttItem();
      });
    });
  }

  // convert tester map to ganttItem to show on gantt chart
  convertTesterToGanttItem(): void {
    const allTesterArray = [...this.loadedTesterMap.values()].flat();
    this.items = allTesterArray.map(tester => ({
      id: tester.id,
      title: tester.name,
      children: this.convertTaskToGanttItem(tester.children || []),
      expanded: tester.expanded,
      expandable: tester.expandable,
    }));
    this.items = [...this.items];
  }

  // convert task map to ganttItem to show on gantt chart
  convertTaskToGanttItem(task: Task[]): GanttItem[] {
    let children: GanttItem[] = [];
    task.forEach(taskData => {
      let croppedStart = 0;
      let croppedEnd = 0;
      if (taskData.name !== 'Load More') {
        croppedStart = Math.max(new Date(taskData.startDate).getTime(), new Date(this.ganttComponent?.view.start.getUnixTime() * 1000).getTime());
        croppedEnd = Math.min(new Date(taskData.endDate).getTime(), new Date(this.ganttComponent?.view.end.getUnixTime() * 1000).getTime());
      }
      children.push({
        id: taskData.id,
        title: taskData.name,
        start: croppedStart,
        end: croppedEnd
      });
    });
    return children;
  }

  isNotSelected(value: number, currentValue: number | null): boolean {
    // disabled option
    return this.selectedUserIds.includes(value) && value !== currentValue;
  }

  startEdit(): void {
    this.isEditing = true;
    this.currentValue = JSON.parse(JSON.stringify(this.initValue || []));
    this.getSelectUserIds();
  }

  removeRow(rowIndex: number): void {
    this.setNoDataText();
    this.currentValue.splice(rowIndex, 1);
    this.getSelectUserIds();
    this.updateTesterListGantt();
    this.getUpdatedDateRange();
  }

  getUpdatedDateRange() {
    // resets range to current day
    let earliest: GanttDate | null = null;
    let latest: GanttDate | null = null;
    this.viewType = GanttViewType.hour;

    this.currentValue.forEach((user) => {
      user.timeIntervals.forEach((timeInterval) => {
        if (timeInterval.startDate && timeInterval.endDate) {
          let selectedStartDate = new GanttDate(timeInterval.startDate);
          let selectedEndDate = new GanttDate(timeInterval.endDate);

          if (!earliest || selectedStartDate.getUnixTime() < earliest.getUnixTime()) {
            earliest = selectedStartDate;
          }
          if (!latest || selectedEndDate.getUnixTime() > latest.getUnixTime()) {
            latest = selectedEndDate;
          }
        }
      });
    });

    if (earliest && latest) {
      this.earliestStart = earliest;
      this.latestEnd = latest;
      setTimeout(() => {
        this.ganttComponent?.scrollToDate(this.earliestStart);
      }, 200);
    } else {
      this.earliestStart = new GanttDate(dayjs().startOf('day').toDate());
      this.latestEnd = new GanttDate(dayjs().endOf('day').toDate());
    }

    // determine view type based on date range
    const diff = this.latestEnd.getUnixTime() - this.earliestStart.getUnixTime();
    if (diff > 2629800 * 6) {
      this.viewType = GanttViewType.month;
    } else if (diff > 86400 * 4) {
      this.viewType = GanttViewType.day;
    } else {
      this.viewType = GanttViewType.hour;
    }
    this.viewOptions = {
      ...this.viewOptions,
      start: this.earliestStart,
      end: this.latestEnd,
      baseTime: this.earliestStart,
      addAmount: 0
    };
    this.updateTasksByDateRange();
  }

  addTester(): void {
    if (!this.isEditing) {
      this.startEdit();
    }
    this.currentValue.push({
      testerId: null,
      timeIntervals: [
        {
          startDate: null,
          endDate: null
        }
      ]
    });
  }

  showLoadMore(resourceId: number): boolean {
    const testerIdSplit = Number(resourceId.toString().split('-')[1]);
    const testerMap = this.loadedTesterMap.get(testerIdSplit.toString());
    return testerMap?.some(tester => tester.hasLoadMore) || false;
  }

  loadMoreChild(testerId: number) {
    const testerIdSplit = Number(testerId.toString().split('-')[1]);

    this.loadedTesterMap.get(testerIdSplit.toString())?.forEach(tester => {
      tester.taskStartRow += 50;
      tester.taskEndRow += 50;
    })
    this.updateTasksByDateRange();
  }

  addTime(testerIndex: number, timeIndex: number): void {
    const addedItem = {
      startDate: null,
      endDate: null
    };
    this.currentValue[testerIndex].timeIntervals.splice(timeIndex + 1, 0, addedItem);
  }

  removeTime(testerIndex: number, timeIndex: number): void {
    this.currentValue[testerIndex].timeIntervals.splice(timeIndex, 1);
    this.getUpdatedDateRange();
  }

  /** 获取存在数据的行 */
  getDataRow(): TaskTester[] {
    return (this.currentValue).map((it, index) => {
      return {
        ...it,
        rowIndex: index + 1
      };
    }).filter(it => it.testerId || (it.timeIntervals || []).some(x => x.startDate || x.endDate));
  }

  /** 获取错误行的信息 */
  getValidRowMsg(rowIndex: number, type: string = 'tester'): string {
    const msg = `${this.fieldName}: The ${type} in row ${rowIndex} is empty.`
    return msg;
  }

  /** 获取错误时间的信息 */
  getValidIntervalMsg(rowIndex: number, timeIndex: number, type: string = 'start date'): string {
    const msg = `${this.fieldName}: The ${type} in row ${rowIndex} is empty.`;
    return msg;
  }

  validForm(): boolean {
    const data = this.getDataRow();
    console.log('data', data);
    // 获取填了数据的行
    if (data.length === 0 && !this.isRequired) {
      return true;
    }
    if (data.length === 0 && this.isRequired) {
      this.message.warning('The Tester is required.');
      return false;
    }
    let flag = true;
    for (let i = 0; i < data.length; i++) {
      const { testerId, timeIntervals } = data[i];
      if (!testerId) {
        flag = false;
        this.message.warning(this.getValidRowMsg(data[i]?.rowIndex as number, 'tester'));
        break;
      }
      for (let j = 0; j < timeIntervals.length; j++) {
        const { startDate, endDate } = timeIntervals[j];
        const groupIndex = data[i]?.rowIndex as number;
        if (!startDate || !endDate) {
          flag = false;
          this.message.warning(this.getValidIntervalMsg(groupIndex, j + 1, !startDate ? 'start date' : 'end date'));
          break;
        }
        if (dayjs(startDate).startOf('hour').valueOf() >= dayjs(endDate).startOf('hour').valueOf()) {
          flag = false;
          this.message.warning(`${this.fieldName}: The start date in row ${groupIndex} should not be later than the end date`);
          break;
        }
        if (flag && this.isOverlapping(timeIntervals.filter(interval => interval.startDate && interval.endDate) as { startDate: string; endDate: string }[])) {
          flag = false;
          this.message.warning(`The tester in row ${data[i]?.rowIndex} has overlapping periods.`);
          break;
        }
      }
    }
    return flag;
  }

  isOverlapping(timeIntervals: { startDate: string; endDate: string }[]): boolean {
    const sortedIntervals = timeIntervals.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    for (let i = 0; i < sortedIntervals.length - 1; i++) {
      const currentEnd = new Date(sortedIntervals[i].endDate).getTime();
      const nextStart = new Date(sortedIntervals[i + 1].startDate).getTime();

      if (currentEnd > nextStart) {
        return true;
      }
    }

    return false;
  }

  handleSave(): void {
    const valid = this.validForm();
    if (this.anyInactiveMembers()) {
      this.message.warning('Please remove or replace inactive users before saving.');
      return;
    }
    if (!valid) {
      return;
    }
    this.confirmationService.confirmUpdateTask(this.inactiveUsers, () => {
        this.isEditing = false;
        if (this.initValue !== this.currentValue) {
          const data = this.getParams();
          this.editSuccess.emit(data);
          data.forEach((param) => {
            this.loadedTaskMap.delete(param.testerId!.toString());
          });
        }
        this.showGantt = false;
        this.items = [...this.items];
      },
    );
  }

  getParams(): TaskTester[] {
    const arr: TaskTester[] = [];
    const hasDataRow = this.getDataRow();

    hasDataRow.forEach(it => {
      const { testerId, timeIntervals } = it;
      const testerInfo = this.accountData?.[testerId as number] || {} as Account;

      // Modify timeIntervals to set minutes, seconds, and milliseconds to 0
      if (timeIntervals && Array.isArray(timeIntervals)) {
        timeIntervals.forEach((interval: any) => {
          if (interval.startDate) {
            interval.startDate = dayjs(interval.startDate)
              .set('minute', 0)
              .set('second', 0)
              .set('millisecond', 0)
              .toISOString();
          }
          if (interval.endDate) {
            interval.endDate = dayjs(interval.endDate)
              .set('minute', 0)
              .set('second', 0)
              .set('millisecond', 0)
              .toISOString();
          }
        });
      }

      arr.push({
        testerId,
        testerName: testerInfo.fullName,
        timeIntervals
      } as TaskTester);
    });

    return arr;
  }


  handleCancel(): void {
    this.isEditing = false;
    this.currentValue = this.value;
    this.showGantt = false;
    console.log('save', this.showGantt);
    this.items = [...this.items];
  }

  validTime(testerIndex: number, timeIndex: number, changeType: string = 'start'): boolean {
    const timeInterval = this.currentValue[testerIndex].timeIntervals[timeIndex];
    console.log('timeInterval', timeInterval);
    const { startDate, endDate } = timeInterval;
    if (startDate && endDate && dayjs(startDate).startOf('hour').valueOf() >= dayjs(endDate).startOf('hour').valueOf()) {
      const msg = changeType === 'start' ? 'The start date should not be later than the end date' : 'The due date should not be earlier than start date';
      this.message.warning(msg);
      return false;
    }
    return true;
  }

  // changeStartTime(testerIndex: number, timeIndex: number): void {
  //   const valid = this.validTime(testerIndex, timeIndex, 'start');
  //   if (!valid) {
  //     setTimeout(() => {
  //       this.currentValue[testerIndex].timeSegments[timeIndex].startTime = null;
  //     });
  //   }
  // }
  //
  //
  // changeDueTime(testerIndex: number, timeIndex: number): void {
  //   const valid = this.validTime(testerIndex, timeIndex, 'end');
  //   console.log('changeDueTime', testerIndex, timeIndex, valid);
  //   if (!valid) {
  //     setTimeout(() => {
  //       this.currentValue[testerIndex].timeSegments[timeIndex].dueTime = null;
  //     });
  //   }
  // }
}
